import { useState, useEffect } from "react";
import { Menu, X, Search, MessageCircle, MapPin } from "lucide-react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { LOGO_WORDMARK, WHATSAPP_URL } from "../../data/promill";

const NAV = [
    { label: "Home", to: "/" },
    { label: "Menu", to: "/menu" },
    { label: "Our Approach", to: "/food-promise" },
    { label: "About", to: "/about" },
    { label: "Franchise", to: "/franchise" },
    { label: "Nutrition", to: "/nutrition" },
    { label: "Contact", to: "/contact" },
];

export const Header = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => setOpen(false), [location.pathname]);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-header"
            className={`sticky top-0 z-50 transition-all duration-300 ${
                scrolled ? "bg-white/95 backdrop-blur-md shadow-sm" : "bg-pm-cream"
            } border-b border-pm-black/10`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex h-16 items-center justify-between gap-4">
                    <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group shrink min-w-0">
                        <img
                            src={LOGO_WORDMARK}
                            alt="ProMill"
                            className="h-7 sm:h-8 lg:h-9 w-auto max-w-[150px] sm:max-w-none object-contain group-hover:scale-[1.03] transition-transform"
                        />
                    </Link>

                    <nav className="hidden xl:flex items-center gap-0.5">
                        {NAV.map((n) => (
                            <NavLink
                                key={n.label}
                                to={n.to}
                                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                                className={({ isActive }) =>
                                    `relative font-semibold text-[13px] px-3 py-2 transition-colors ${
                                        isActive ? "text-pm-green" : "text-pm-black/80 hover:text-pm-green"
                                    } after:content-[''] after:absolute after:left-3 after:right-3 after:bottom-0.5 after:h-[2px] after:bg-pm-yellow after:transition-transform ${
                                        isActive ? "after:scale-x-100" : "after:scale-x-0 hover:after:scale-x-100"
                                    } after:origin-left`
                                }
                            >
                                {n.label}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="flex items-center gap-2">
                        <Link
                            to="/contact"
                            data-testid="header-waitlist-icon"
                            aria-label="Join Waitlist"
                            className="hidden sm:flex h-10 w-10 items-center justify-center rounded-full border border-pm-black/15 hover:bg-pm-yellow/20 hover:border-pm-yellow transition-colors"
                        >
                            <MapPin className="w-4 h-4 text-pm-green" />
                        </Link>
                        <button
                            data-testid="search-button"
                            aria-label="Search"
                            className="hidden sm:flex h-10 w-10 items-center justify-center rounded-full border border-pm-black/15 hover:bg-pm-yellow/20 hover:border-pm-yellow transition-colors"
                        >
                            <Search className="w-4 h-4 text-pm-black/70" />
                        </button>
                        <a
                            href={WHATSAPP_URL}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="enquire-now-button"
                            className="hidden"
                        >
                            <MessageCircle className="w-4 h-4" />
                            Enquire Now
                        </a>
                        <button
                            data-testid="mobile-menu-toggle"
                            aria-label="Toggle menu"
                            className="xl:hidden h-10 w-10 flex items-center justify-center rounded-full border border-pm-black/15"
                            onClick={() => setOpen(!open)}
                        >
                            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                        </button>
                    </div>
                </div>
            </div>

            {open && (
                <div data-testid="mobile-menu" className="xl:hidden border-t border-pm-black/10 bg-pm-cream">
                    <div className="px-4 py-4 flex flex-col gap-1">
                        {NAV.map((n) => (
                            <NavLink
                                key={n.label}
                                to={n.to}
                                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={({ isActive }) =>
                                    `font-semibold px-4 py-3 rounded-lg ${
                                        isActive ? "bg-pm-yellow/30 text-pm-green" : "text-pm-black hover:bg-pm-yellow/15"
                                    }`
                                }
                            >
                                {n.label}
                            </NavLink>
                        ))}
                        <a
                            href={WHATSAPP_URL}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="mobile-enquire-button"
                            className="hidden"
                        >
                            <MessageCircle className="w-4 h-4" />
                            Enquire Now
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
};
